<template lang="pug">
form.screen-slider.w-full
  .flex.flex-col.fade-in(:class="[center ? 'items-center mx-auto' : 'items-start']")
    uc-editable.font-bold(
      :k="`${id}.question`"
      :editPath="`${editPath}.question`"
      :text="question"
    )
  label.block(
    :class="[largerThanSm ? 'mt-16' : 'mt-6']"
  )
    .fade-in(v-if="!audioDoc")
      .bg-paper.text-center.w-full(
        :class="[largerThanSm ? 'p-10' : 'p-6']"
      )
        p.relative
          output.teardrop(
            :style="{ left: leftSize }" 
            uc-word-skip="" 
            v-if="largerThanSm" 
            :data-test="largerThanSm"
          ) 
            span.output_text {{ abbrValue() }}{{ noPercent ? '' : '%' }}
          output(v-else="")
            uc-text.min-label(
              uc-word-skip=""
              :text="`${abbrValue()}${noPercent ? '' : '%'}`"
              )
          .flex.flex-row.gap-1
            uc-text.min-label.break-words(
              class="max-w-[8ch]"
              :k="`${id}.min-label`"
              :text="this.minLabel ? this.minLabel : `${Number(min).toLocaleString()} ${noPercent ? '' : '%'}`"
            )
            uc-preload-sfx(name="slider")
            input.flex-4(
              :disabled="isSubmitted"
              :id="`${id}_slider_input`"
              :max="max"
              :min="min"
              :step="step"
              :style="{ backgroundSize: backgroundSize }"
              @input="updateSlider"
              type="range"
              uc-word-skip=""
              v-model="value"
              data-sfx="slider"
            )
            span(uc-word-only="") –
            uc-text.max-label.break-words(
              class="max-w-[8ch]"
              :k="`${id}.max-label`"
              :text="this.maxLabel ? this.maxLabel : `${Number(max).toLocaleString()} ${noPercent ? '' : '%'}`"
            )
        .group.mt-8.mx-auto.slider__submit-btn
          uc-button.btn.btn-primary.slider__submit.ripple(
            :disabled="isSubmitted"
            @click="submitHandler"
            ref="submit"
          )
            span.ripple__wave 
            uc-text(text="Submit")

.slider__feedback.mx-auto.flex-1.fade-in(
  ref="feedbackRef" 
  v-if="this.feedback"
  aria-live="polite"
  aria-atomic="true"
  role="status"
  )
  audio-marker(:name="`${this.id}-feedback`")

  transition-expand(:delay="0.75" :duration="0.75" scroll="")
    .slider__feedback-content.outline-2.block(
      v-if="isSubmitted || wordDoc || audioDoc"
    )
      p.prose.md_prose-lg.text-primary-700.dark_text-primary-300.text-center.font-bold.feedback-heading.mb-4
        //- Only show 'Answer' if a correct answer exists
        div(v-if="correct")
          uc-text(
            :values="{ answer: Number(correct).toLocaleString() + (noPercent ? '' : '%') }"
            text="Answer: ${answer}"
          )
        //- Otherwise, show 'Feedback'
        div(v-else-if="!audioDoc")
          uc-text(text="Feedback")
      uc-editable.md_prose-base.text-center(
        :k="`${id}.feedback`"
        :editPath="`${editPath}.feedback`"
        :text="feedback"
      )
      slot
</template>

<script lang="ts">
import { set, setCompleted } from '../lib/SCORM';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
import { nextTick, computed, toRef } from 'vue';
import gsap from 'gsap';
import { saveInsightsData } from '../lib/insightsData';
import useScreenAudio from '../lib/useScreenAudio';
import sliderSchema from '../lib/screen-schemas/screen-slider.schema';

export default {
  inject: ['sectionId', 'suspend', 'wordDoc', 'audioDoc'],
  inheritAttrs: false,
  props: {
    editPath: { type: String, required: false },
    id: { type: String, default: '' },
    center: { type: Boolean, required: false },
    min: { type: Number, required: false, default: 0 },
    max: { type: Number, required: false, default: 100 },
    step: { type: Number, required: false, default: 1 },
    correct: { type: Number, required: false, default: null },
    feedback: { type: String, required: false, default: null },
    question: { type: String, default: '' },
    noPercent: { type: Boolean, required: false },
    'min-label': { type: String, required: false, default: null },
    'max-label': { type: String, required: false, default: null },
    screen: { type: Object, required: false, default: null }
  },
  setup(props) {
    const breakpoints = useBreakpoints(breakpointsTailwind);
    const largerThanSm = breakpoints.greater('sm');

    // Build a minimal narration context from this component's own props so that
    // useScreenAudio can derive the correct audio key (id) and JiT text (feedback).
    // Ref + computed so live patches (new screen objects) and fallback prop edits stay current.
    const screenRef = toRef(props, 'screen');
    const narrationCtx = computed(
      () => screenRef.value ?? { id: props.id, feedback: props.feedback }
    );
    const { playSlot } = useScreenAudio(narrationCtx, sliderSchema);

    return { largerThanSm, playSlot };
  },
  data() {
    return {
      value: this.min,
      backgroundSize: '0% 100%',
      leftSize: 'calc(0% + 103px)'
    };
  },
  computed: {
    isCorrect() {
      return parseInt(this.value) === this.correct;
    },
    isSubmitted() {
      return Boolean(this.suspend.completed[this.id]);
    }
  },
  methods: {
    updateSlider(e) {
      let clickedElement = e.target,
        min = clickedElement.min,
        max = clickedElement.max,
        val = clickedElement.value,
        percent = ((val - min) * 100) / (max - min),
        leftAdjust = (percent / 100) * 232;

      this.backgroundSize = percent + '% 100%';
      this.leftSize = 'calc(' + percent + '% + (103px - ' + leftAdjust + 'px))';
    },
    async submitHandler(e) {
      this.backgroundSize =
        ((this.correct - this.min) * 100) / (this.max - this.min) + '% 100%';

      setCompleted(this.id);

      await nextTick();

      set(`questionOptions.${this.id}`, this.value);
      set(`correctQuestions.${this.id}`, this.isCorrect);

      await new Promise(r => gsap.delayedCall(this.feedback ? 0.5 : 0, r));

      setCompleted(this.sectionId);

      saveInsightsData(this.screen || this, {
        correct: this.isCorrect
      });

      if (this.feedback) this.playSlot('feedback');
    },
    abbrValue() {
      var number = this.value;

      // 2 decimal places => 100, 3 => 1000, etc
      var decPlaces = Math.pow(10, 1);

      // Enumerate number abbreviations
      var abbrev = ['K', 'M', 'B', 'T'];

      // Go through the array backwards, so we do the largest first
      for (var i = abbrev.length - 1; i >= 0; i--) {
        // Convert array index to "1000", "1000000", etc
        var size = Math.pow(10, (i + 1) * 3);

        // If the number is bigger or equal do the abbreviation
        if (size <= number) {
          // Here, we multiply by decPlaces, round, and then divide by decPlaces.
          // This gives us nice rounding to a particular decimal place.
          number = Math.round((number * decPlaces) / size) / decPlaces;

          // Handle special case where we round up to the next abbreviation
          if (number === 1000 && i < abbrev.length - 1) {
            number = 1;
            i++;
          }

          // Add the letter for the abbreviation
          number += abbrev[i];

          // We are done... stop
          break;
        }
      }

      return number;
    }
  }
};
</script>

<style scope>
.min-label,
.max-label {
  min-width: 100px;

  @apply prose prose text-primary-500 text-primary-500 flex-none
 flex-none self-center self-center font-bold;
}

/* Below CSS based on https://codepen.io/irozaih/pen/bmxMKy */

.screen-slider input[type='range'] {
  @apply focus-visible_ring;
  -webkit-appearance: none;
  display: block;
  width: 100%;
  height: 8px;
  margin: 20px 0;
  background: rgb(var(--color-gray-300));
  background-image: -webkit-gradient(
    linear,
    20% 0%,
    20% 100%,
    color-stop(0%, rgb(var(--color-secondary-500))),
    color-stop(100%, rgb(var(--color-secondary-500)))
  );
  background-repeat: no-repeat;
  border-radius: 20px;
  /* transition-duration: 75ms; */
}

.screen-slider input[type='range']::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  cursor: pointer;
  box-shadow: none;
  background: transparent;
  border-radius: 0px;
  border: none;
}

.screen-slider input[type='range']:disabled::-webkit-slider-runnable-track {
  cursor: default;
}

.screen-slider input[type='range']::-webkit-slider-thumb {
  box-shadow: none;
  border: 4px solid rgb(var(--color-primary-500));
  height: 28px;
  width: 28px;
  border-radius: 50%;
  background: rgb(var(--color-primary-500));
  cursor: grab;
  -webkit-appearance: none;
  margin-top: -10px;

  &:active {
    cursor: grabbing;
  }
}

.screen-slider input[type='range']:disabled::-webkit-slider-thumb {
  border: 4px solid rgb(var(--color-gray-400));
  background: rgb(var(--color-gray-400));
  cursor: default;
}

.screen-slider input[type='range']:focus::-webkit-slider-runnable-track {
  background: transparent;
}

.screen-slider input[type='range']::-moz-range-track {
  width: 100%;
  height: 4px;
  cursor: pointer;
  box-shadow: none;
  background: transparent;
  border-radius: 0px;
  border: none;
}

.screen-slider input[type='range']:disabled::-moz-range-track {
  cursor: auto;
}

.screen-slider input[type='range']::-moz-range-thumb {
  box-shadow: none;
  border: 4px solid rgb(var(--color-primary-500));
  height: 28px;
  width: 28px;
  border-radius: 50%;
  background: rgb(var(--color-primary-500));
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.screen-slider input[type='range']:disabled::-moz-range-thumb {
  border: 4px solid rgb(var(--color-gray-400));
  background: rgb(var(--color-gray-400));
  cursor: default;
}

/* Below CSS based on: https://codepen.io/EskoCruz/pen/OVgZqX */

.screen-slider .teardrop {
  position: absolute;
  width: 65px;
  height: 65px;
  top: -45px;
  background: rgb(var(--color-primary-500));
  transform: perspective(50px) rotateX(10deg) rotateZ(-45deg);
  transform-origin: 50% 50%;
  border-radius: 50% 50% 50% 0;
  padding: 0 3px 3px 0;
  margin: -2.2em 0 0 -1.3em;
  box-shadow: -1px 1px 4px rgba(0, 0, 0, 0.5);
}

.screen-slider .output_text {
  color: #fff;
  padding: 20px;
  border-radius: 50px;
  font-weight: 700;
  position: absolute;
  transform: translateX(-55%) translateY(-40%) perspective(120px) rotateX(0deg)
    rotateZ(45deg) rotateX(-20deg) translateY(-2%);
  transform-origin: 50% 50%;
  top: 50%;
  left: 50%;
}
</style>
